import {
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { NotificationPolicyTypeEnum, NotificationType } from '@repo/api-gw-sdk';
import { useState } from 'react';

import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { supportedNotificationTypes, NotificationTypes } from './data';
import type { UpsertNotificationPolicyState } from './upsertNotificationPolicyFlow';

import { StepContainer } from '../wizard/StepContainer';
import { RenameEntityName } from '../wizard/renameEntityName';

export const NotificationPolicyNotifications = (
  props: StepProps<UpsertNotificationPolicyState>
) => {
  const dal = useDAL();
  const [error, setError] = useState<string | undefined>();
  const [saveInProgress, setSaveInProgress] = useState(false);

  return (
    <StepContainer
      hideStepper
      onBackClick={props.back}
      canGoNext={() =>
        !saveInProgress && props.wizardState.notificationTypes?.length > 0
      }
      nextButtonText={
        props.wizardState.id
          ? 'Save'
          : props.wizardState.type === NotificationPolicyTypeEnum.Slack
            ? 'Test and Create'
            : 'Create'
      }
      onNextClick={async () => {
        setSaveInProgress(true);
        setError(undefined);

        try {
          // Filter out empty headers before saving
          let stateToSave = props.wizardState;
          if (
            stateToSave.type === NotificationPolicyTypeEnum.Webhook &&
            stateToSave.config?.webhook?.headers
          ) {
            const filteredHeaders = Object.entries(
              stateToSave.config.webhook.headers
            ).reduce<Record<string, string>>((acc, [key, value]) => {
              if (key.trim() && value.trim()) {
                acc[key] = value;
              }
              return acc;
            }, {});

            stateToSave = {
              ...stateToSave,
              config: {
                ...stateToSave.config,
                webhook: {
                  ...stateToSave.config.webhook,
                  headers: filteredHeaders,
                },
              },
            };
          }

          if (props.wizardState.id) {
            await dal.notifications.policies.update(stateToSave);
            await props.finish();
          } else {
            await dal.notifications.policies.create(stateToSave);
            await props.finish();
          }
        } catch (e) {
          const errorMessage =
            (e as { body?: { error?: string } })?.body?.error ||
            (e as Error).message ||
            'Unknown error';
          setError(`Failed to save notification policy: ${errorMessage}`);
        } finally {
          setSaveInProgress(false);
        }
      }}
      navigationComponent={
        error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <RenameEntityName
            label='Notification policy name'
            name={props.wizardState.name}
            onSubmit={(name) =>
              props.setWizardState((state) => ({ ...state, name }))
            }
          />
        )
      }
    >
      <Stack className='flex-grow'>
        <Stack
          className='px-[60px] flex-grow'
          gap='24px'
          color={'var(--mui-palette-text-primary)'}
        >
          <Typography>{`Subscribe to these events:`}</Typography>
          <Stack>
            <FormControlLabel
              className='w-full'
              control={
                <Checkbox
                  sx={{ padding: '10px 10px 10px 30px' }}
                  size='small'
                  checked={supportedNotificationTypes.every((x) =>
                    props.wizardState.notificationTypes?.includes(x)
                  )}
                  onChange={(event, value) =>
                    props.setWizardState((state) => {
                      const hasAuditEvent = state.notificationTypes?.includes(
                        NotificationType.AuditLogCreated
                      );
                      return {
                        ...state,
                        notificationTypes: value
                          ? [
                              ...supportedNotificationTypes,
                              ...(hasAuditEvent
                                ? [NotificationType.AuditLogCreated]
                                : []),
                            ]
                          : hasAuditEvent
                            ? [NotificationType.AuditLogCreated]
                            : [],
                      };
                    })
                  }
                />
              }
              label={'Select all'}
            />
            <Divider sx={{ borderStyle: 'dashed', my: '12px' }} />
            {supportedNotificationTypes.map((x) => (
              <FormControlLabel
                key={x}
                className='w-full'
                control={
                  <Checkbox
                    sx={{ padding: '10px 10px 10px 30px' }}
                    size='small'
                    checked={props.wizardState.notificationTypes?.includes(x)}
                    onChange={(event, value) =>
                      props.setWizardState((state) => ({
                        ...state,
                        notificationTypes: [
                          ...(state.notificationTypes || []).filter(
                            (y) => y !== x
                          ),
                          ...(value ? [x] : []),
                        ],
                      }))
                    }
                  />
                }
                label={NotificationTypes[x]?.title}
              />
            ))}
          </Stack>
          {props.wizardState.type === NotificationPolicyTypeEnum.Webhook && (
            <Stack gap='12px' mt='24px'>
              <Divider sx={{ borderStyle: 'dashed' }} />
              <Typography variant='subtitle2' fontWeight='600'>
                Additional Events (Optional)
              </Typography>
              <FormControlLabel
                className='w-full'
                control={
                  <Checkbox
                    sx={{ padding: '10px 10px 10px 30px' }}
                    size='small'
                    checked={props.wizardState.notificationTypes?.includes(
                      NotificationType.AuditLogCreated
                    )}
                    onChange={(event, value) =>
                      props.setWizardState((state) => ({
                        ...state,
                        notificationTypes: value
                          ? [
                              ...(state.notificationTypes || []),
                              NotificationType.AuditLogCreated,
                            ]
                          : (state.notificationTypes || []).filter(
                              (x) => x !== NotificationType.AuditLogCreated
                            ),
                      }))
                    }
                  />
                }
                label='Include audit log events (structured JSON)'
              />
              <Typography
                variant='caption'
                sx={{ ml: '42px', color: 'text.secondary' }}
              >
                Audit events contain detailed JSON logs of all system actions
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
    </StepContainer>
  );
};
